import type { Uuid } from "@/domains/global/identifiers";
import type {
  AssistantSkillModelData,
  AssistantSkillOrigin,
  AssistantSkillStatus,
} from "@/store/assistant/types";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind, type SyncUpdateValue } from "@/store/sync/types";
import type { WithAppStore } from "@/store/types";
import { computed, makeObservable, observable } from "mobx";

export class AssistantSkillObservable extends BaseSyncModel<AssistantSkillModelData> {
  public modelKind = SyncModelKind.AssistantSkill;

  constructor({
    id,
    data,
    store,
  }: { id: Uuid; data: SyncUpdateValue<AssistantSkillModelData> } & WithAppStore) {
    super({ id, data, store });

    makeObservable(this, {
      modelKind: observable,
      assistantId: computed,
      name: computed,
      whenToUse: computed,
      instructions: computed,
      status: computed,
      origin: computed,
      publicId: computed,
      createdAt: computed,
      updatedAt: computed,
      hasQueuedOperations: computed,
    });
  }

  get assistantId(): Uuid {
    return this.modelData.assistant_id;
  }

  get name(): string {
    return this.modelData.name;
  }

  get whenToUse(): string {
    return this.modelData.when_to_use;
  }

  get instructions(): string {
    return this.modelData.instructions;
  }

  get status(): AssistantSkillStatus {
    return this.modelData.status;
  }

  get origin(): AssistantSkillOrigin {
    return this.modelData.origin;
  }

  get publicId(): Uuid | null {
    return this.modelData.public_id;
  }

  get createdAt(): string {
    return this.modelData.created_at;
  }

  get updatedAt(): string {
    return this.modelData.updated_at;
  }

  get hasQueuedOperations(): boolean {
    return (this.store.sync.actionQueue.operationsByModelId.get(this.id)?.length ?? 0) > 0;
  }
}
